import { useState, useRef } from 'react'
import JSZip from 'jszip'
import { parseCSV, getDateRange } from '../utils/csvParser'
import { calculateWeightedPrice } from '../utils/priceCalculator'
import { fetchPrices } from '../utils/api'
import './CSVUpload.css'

function CSVUpload({ onDataLoaded }) {
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState(null)
  const fileInputRef = useRef(null)

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFile(files[0])
    }
  }

  const handleFileSelect = (e) => {
    const files = e.target.files
    if (files.length > 0) {
      handleFile(files[0])
    }
  }

  const handleFile = async (file) => {
    const isZip = file.name.endsWith('.zip')
    const isCsv = file.name.endsWith('.csv')

    if (!isZip && !isCsv) {
      setError('Prašome įkelti CSV arba ZIP failą')
      return
    }

    setIsProcessing(true)
    setError(null)

    try {
      let text = ''
      
      if (isZip) {
        const zip = await JSZip.loadAsync(file)
        // Find the first CSV file in the zip
        const csvFile = Object.values(zip.files).find(f => f.name.toLowerCase().endsWith('.csv'))
        
        if (!csvFile) {
          throw new Error('ZIP archyve nerastas nei vienas CSV failas')
        }
        
        text = await csvFile.async('text')
      } else {
        text = await file.text()
      }

      const parsedData = parseCSV(text)
      
      if (parsedData.length === 0) {
        throw new Error('CSV failas tuščias arba neteisingas formatas')
      }

      // 1. Detect date range
      const { minDate, maxDate } = getDateRange(parsedData)
      
      // 2. Fetch real prices
      const from = minDate.toISOString().split('T')[0]
      const to = maxDate.toISOString().split('T')[0]
      
      let priceData = null
      try {
        const result = await fetchPrices(from, to)
        priceData = result.prices
      } catch (err) {
        console.warn('Nepavyko gauti realių kainų, naudojama pavyzdinė kaina:', err)
      }

      // Calculate statistics
      const statistics = calculateWeightedPrice(parsedData, priceData)

      onDataLoaded({
        consumption: parsedData,
        statistics: statistics,
        prices: priceData
      })

      setIsProcessing(false)
    } catch (err) {
      setError(err.message)
      setIsProcessing(false)
    }
  }

  return (
    <div className="csv-upload">
      <div
        className={`upload-area ${isDragging ? 'dragging' : ''} ${isProcessing ? 'processing' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.zip"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        {isProcessing ? (
          <div className="upload-status">
            <div className="spinner"></div>
            <p>Apdorojama...</p>
          </div>
        ) : (
          <div className="upload-prompt">
            <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="upload-text">
              <strong>Spustelėkite arba nutempkite</strong> CSV arba ZIP failą čia
            </p>
            <p className="upload-hint">
              Palaikomas formatas: mano.eso.lt CSV arba ZIP failas
            </p>
            <div className="upload-privacy">
              <svg className="privacy-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>Jūsų failai nėra įkeliami į serverį – apdorojimas vyksta tik jūsų naršyklėje.</span>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="upload-error">
          <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}

export default CSVUpload
